import { useLazyQuery } from '@apollo/client'
import IconViewEmailByAMS from 'assets/images/icon-email.svg'
import { isAxiosError } from 'axios'
import classNames from 'classnames'
import Loading from 'components/loading'
import ButtonBase from 'components/partials/button'
import DropdownBase from 'components/partials/dropDown'
import { notify } from 'components/toastify'
import { EMAIL_HISTORY_STATUS, styleJsonPacket } from 'constants/common'
import { MESSAGES, STATUS } from 'constants/messages'
import moment from 'moment'
import axiosInstance from 'network/axiosClient'
import DateFilterPicker from 'pages/developer-portal/table/activities-log/component/dateFilterPicker'
import ViewDetailContentItemDialog from 'pages/email-system-management/components/view-detail-content-log'
import SearchInput from 'pages/principal-account-management/components/searchInput'
import { GET_SENT_EMAIL_HISTORY_QUERY } from 'queries/adminPortal'
import React, { memo, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Moment from 'react-moment'
import { IHistoryEmailItem, ISentEmailFilterItem } from '../../interface'
import { tableHeaderEmailHistoryTable } from './headers'
import TotalNumberInTable from 'components/partials/totalNumberInTable'

interface Props {
  setIsRefetchList: (refetchUser: boolean) => void
  isRefetchList: boolean
}

const EmailHistoryTable: React.FC<Props> = () => {
  const prevSearchObjectRef = useRef({})
  const [hasMoreEmailData, setHasMoreEmailData] = useState<boolean>(true)
  const [isOpenModalViewDetailEmail, setIsOpenModalViewDetailJob] = useState<boolean>(false)
  const [currentEmailItemSelected, setCurrentEmailItemSelected] = useState<IHistoryEmailItem>()
  const [listEmailSentHistory, setListEmailSentHistory] = useState<IHistoryEmailItem[]>([])
  const [disableDownloadBtn, setDisableDownloadBtn] = useState<boolean>(false)
  const [searchObject, setSearchObject] = useState<ISentEmailFilterItem>({
    skip: 0,
    take: 20,
    searchValue: '',
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [minDateFilter, setMinDateFilter] = useState<Date>()
  const [maxDateFilter, setMaxDateFilter] = useState<Date>()
  const [isFocusSearchJobIdOrEmail, setIsFocusSearchJobIdOrEmail] = useState<boolean>(false)

  const [
    getSentEmailHistory,
    { loading: getEmailSentHistoryLoading, fetchMore: fetchMoreForGetEmailSentHistories },
  ] = useLazyQuery(GET_SENT_EMAIL_HISTORY_QUERY)

  const fetchMoreDataForEmailHistory = async () => {
    try {
      const response = await fetchMoreForGetEmailSentHistories({
        variables: {
          filter: {
            ...searchObject,
            skip: listEmailSentHistory.length,
            take: 20,
          },
        },
      })
      const { data, total } = response.data.getSentEmailHistory
      setTotalCount(total)
      if (data.length > 0) {
        setHasMoreEmailData(data.length === 20)
        const dataState = [...listEmailSentHistory, ...data]
        setTimeout(() => {
          setListEmailSentHistory(dataState)
        }, 1000)
      } else {
        setHasMoreEmailData(false)
        return
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const handleGetData = async (searchObject: ISentEmailFilterItem) => {
    try {
      const response = await getSentEmailHistory({
        variables: {
          filter: searchObject,
        },
        fetchPolicy: 'network-only',
      })
      if (response.data.getSentEmailHistory) {
        const { data, total } = response.data.getSentEmailHistory
        setTotalCount(total)
        setListEmailSentHistory(data)
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const handleCloseEmailInfoDialog = () => {
    setIsOpenModalViewDetailJob(false)
  }

  const onHandleClickViewDetailJobAction = () => {
    setIsOpenModalViewDetailJob(true)
  }

  const startDateChange = (value: Date) => {
    setMinDateFilter(value)
    const dateFrom = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateFrom, take: 20, skip: 0 }
    })
  }

  const endDateChange = (value: Date) => {
    setMaxDateFilter(value)
    const dateTo = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateTo, take: 20, skip: 0 }
    })
  }

  const handleSearchJobIdAndEmail = (value: string) => {
    setSearchObject((state) => {
      return { ...state, searchValue: value.trim(), take: 20, skip: 0 }
    })
  }

  useEffect(() => {
    if (
      searchObject &&
      JSON.stringify(searchObject) !== JSON.stringify(prevSearchObjectRef.current)
    ) {
      setHasMoreEmailData(true)
      handleGetData(searchObject)
    }
    prevSearchObjectRef.current = searchObject
  }, [searchObject])

  const getStatusValue = (val: any) => {
    if (val.name === 'ALL') {
      setSearchObject((state) => {
        delete state.emailStatus
        return { ...state }
      })
    } else {
      setSearchObject((state) => {
        return { ...state, emailStatus: val.name }
      })
    }
  }

  const onClickDownloadCSVBtn = async () => {
    try {
      setDisableDownloadBtn(true)
      const response = await axiosInstance.post('/export/sent/email', searchObject, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob)

      // Create a download link and initiate the file download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = 'sentEmail.csv'
      downloadLink.click()
    } catch (error: Error | any) {
      if (isAxiosError(error)) {
        notify(error.message, STATUS.ERROR)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    } finally {
      setDisableDownloadBtn(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-headline5A text-neutral-1">Email History</p>
        <div className="flex items-end space-x-4">
          <TotalNumberInTable number={totalCount} />
          <ButtonBase
            className="disabled:bg-primary-shade3 mr-2"
            bgHover="Download detail email job csv"
            onClick={onClickDownloadCSVBtn}
            disabled={disableDownloadBtn}
          >
            Download CSV
          </ButtonBase>
        </div>
      </div>
      <div className="flex w-full mb-5  space-x-1">
        <DateFilterPicker
          className="w-1/4"
          setCallBackDateChange={startDateChange}
          maxDate={maxDateFilter}
          defaultDate={moment().subtract(1, 'month').toDate()}
        />
        <DateFilterPicker
          className="w-1/4"
          setCallBackDateChange={endDateChange}
          minDate={minDateFilter}
        />
        <div className="w-1/4">
          <DropdownBase
            buttonClassName="h-[58px]"
            options={EMAIL_HISTORY_STATUS.concat({ id: 'ALL', name: 'ALL' })}
            handleOptions={getStatusValue}
            initialValue={'ALL'}
          />
        </div>
        <div
          className={classNames('rounded-xl border-[2px] w-1/4', {
            'border-neutral-3': isFocusSearchJobIdOrEmail,
            'border-neutral-5': !isFocusSearchJobIdOrEmail,
          })}
        >
          <SearchInput
            classInput="border-0 w-full"
            setIsFocus={setIsFocusSearchJobIdOrEmail}
            handleSearch={handleSearchJobIdAndEmail}
            placehoderText="Search box jobId or email"
            isHandleSearch={false}
          />
        </div>
      </div>
      <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
          <tr>
            {tableHeaderEmailHistoryTable.map((item: any, idx) => (
              <th key={idx} className={`font-semibold py-5 pl-6 ${item.styles}`}>
                <div className={`flex`}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                  <div className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
      {getEmailSentHistoryLoading && <Loading className="relative py-6" height={30} width={30} />}

      {listEmailSentHistory?.length === 0 && !getEmailSentHistoryLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        Boolean(
          listEmailSentHistory !== null &&
            listEmailSentHistory?.length > 0 &&
            !getEmailSentHistoryLoading,
        ) && (
          <InfiniteScroll
            dataLength={listEmailSentHistory?.length}
            next={fetchMoreDataForEmailHistory}
            hasMore={hasMoreEmailData}
            height={'max-content'}
            style={{ maxHeight: 500 }}
            loader={''}
            endMessage={''}
            className="cus-scrollbar"
          >
            {listEmailSentHistory?.map((sentEmailHistory: IHistoryEmailItem, index: number) => (
              <table key={index} className="cursor-pointer w-full">
                <tbody>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td
                      className={` ${tableHeaderEmailHistoryTable[0].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{sentEmailHistory.jobId}</span>
                    </td>
                    <td
                      className={`${tableHeaderEmailHistoryTable[1].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{sentEmailHistory.to}</span>
                    </td>
                    <td
                      className={`${
                        tableHeaderEmailHistoryTable[2].rowWid
                      } py-5 break-all pl-6 md:pl-3 align-top ${'md:pl-[20px]'}`}
                    >
                      <span>
                        {
                          <Moment format="yyyy-MM-DD HH:mm">
                            {moment.utc(sentEmailHistory.sent_date ?? '')}
                          </Moment>
                        }
                      </span>
                    </td>
                    <td
                      className={`${tableHeaderEmailHistoryTable[3].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{sentEmailHistory.subject}</span>
                    </td>
                    <td
                      className={`${tableHeaderEmailHistoryTable[4].rowWid} lg:pl-8 pl-1 md:${tableHeaderEmailHistoryTable[3].rowWid} align-top`}
                    >
                      <div className="relative w-full break-all ">
                        <div className="h-[86px]">
                          <p
                            dangerouslySetInnerHTML={{ __html: sentEmailHistory.content }}
                            style={{ ...styleJsonPacket }}
                            className={`break-all lg:pl-2 pt-[19px] pl-6 pr-6`}
                          ></p>
                          <button
                            title="View Email"
                            onClick={(e) => {
                              e.stopPropagation()
                              onHandleClickViewDetailJobAction()
                              setCurrentEmailItemSelected(sentEmailHistory)
                            }}
                          >
                            <img
                              src={IconViewEmailByAMS}
                              alt="Icon-Hover-Copy"
                              className="absolute top-[24px] right-0"
                            />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td
                      className={`${tableHeaderEmailHistoryTable[5].rowWid} py-5 pl-8 break-all align-top`}
                    >
                      <span
                        className={`${
                          sentEmailHistory.status === 'SENT' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {' '}
                        {sentEmailHistory?.status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}
          </InfiniteScroll>
        )
      )}

      {isOpenModalViewDetailEmail && (
        <ViewDetailContentItemDialog
          key={currentEmailItemSelected?.jobId}
          modalIsOpen={true}
          title="View Email Detail"
          handleCloseDialog={handleCloseEmailInfoDialog}
          contentHtml={currentEmailItemSelected?.content || ''}
        />
      )}
    </div>
  )
}
export default memo(EmailHistoryTable)
